import { Controller } from '@hotwired/stimulus'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

// Connects to data-controller="export-link"
//
// Keeps an Export link in sync with the filter state of a sibling turbo-frame
// WITHOUT advancing the page URL. After a frame navigates, Turbo sets the
// frame's `src` attribute to the loaded URL — we read filter params from
// there. On initial page load `src` is empty, so we fall back to the address
// bar so deep-linked filters still hydrate the button correctly.
//
// Values:
//   base          — path the CSV download should target (sans query string)
//   frame         — id of the turbo-frame to observe
//   defaultLabel  — label to show when no month is selected
export default class extends Controller {
  static values = {
    base: String,
    frame: String,
    defaultLabel: { type: String, default: 'Export' }
  }

  connect () {
    this.boundFrameLoad = this.onFrameLoad.bind(this)
    document.addEventListener('turbo:frame-load', this.boundFrameLoad)
    this.sync()
  }

  disconnect () {
    document.removeEventListener('turbo:frame-load', this.boundFrameLoad)
  }

  onFrameLoad (event) {
    if (this.hasFrameValue && event.target.id === this.frameValue) {
      this.sync()
    }
  }

  sync () {
    const target = new URL(this.baseValue, window.location.origin)
    const source = new URLSearchParams(this.currentSearch())

    for (const [key, value] of source) {
      target.searchParams.set(key, value)
    }

    target.searchParams.delete('page')
    target.searchParams.delete('format')
    target.searchParams.set('format', 'csv')

    this.element.href = `${target.pathname}?${target.searchParams.toString()}`
    this.element.textContent = this.labelFor(target.searchParams.get('month'))
  }

  currentSearch () {
    if (this.hasFrameValue) {
      const frame = document.getElementById(this.frameValue)
      if (frame?.src) {
        return new URL(frame.src, window.location.origin).search
      }
    }
    return window.location.search
  }

  labelFor (monthKey) {
    if (!monthKey) return this.defaultLabelValue

    const match = /^(\d{4})-(\d{2})$/.exec(monthKey)
    if (!match) return this.defaultLabelValue

    const year = parseInt(match[1], 10)
    const monthIdx = parseInt(match[2], 10) - 1
    if (monthIdx < 0 || monthIdx > 11) return this.defaultLabelValue

    return `Export ${MONTHS[monthIdx]} ${year}`
  }
}
